import React, { useMemo } from "react";
import { Info } from "lucide-react";
import { Badge } from "@/components/Badge/Badge";
import DataTable from "@/components/Datatable/Datatable";
import { Column } from "@/components/Datatable/types";
import SearchInput from "@/components/SearchInput/SearchInput";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { AdoptionConnection } from "../../api/getAdoptionConnections";
import { useAdoptionConnections } from "../../hooks/useAdoptionConnections";
import AttributionDialog from "../AttributionDialog/AttributionDialog";

interface ConnectionsTableProps {
  staleOnly?: boolean;
  noDataMessage?: string;
}

const formatDay = (value: string | null): string => {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return "";
  }
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
};

const connectionStatus = (connection: AdoptionConnection): string => {
  if (connection.disconnectedAt) {
    return "Disconnected";
  }
  if (connection.isStale) {
    return "Stale";
  }
  return "Connected";
};

const attributionLabel = (connection: AdoptionConnection): string => {
  if (connection.attributionSource === "self") {
    return "Self";
  }
  if (connection.attributionSource === "coach") {
    return connection.coachName ? `Coach - ${connection.coachName}` : "Coach";
  }
  return "Unset";
};

function createConnectionColumns(
  onEditAttribution: (connection: AdoptionConnection) => void,
): Column<AdoptionConnection>[] {
  return [
    {
      accessorKey: "companyName",
      header: "Company",
      enableSorting: true,
      cell: ({ row }) => (
        <span className="text-gray-900 dark:text-white">
          {row.original.companyName}
        </span>
      ),
    },
    {
      accessorKey: "intacctId",
      header: "Intacct ID",
      cell: ({ row }) => (
        <span className="text-gray-500 dark:text-gray-400">
          {row.original.intacctId}
        </span>
      ),
    },
    {
      accessorKey: "firstConnectedAt",
      header: () => (
        <span className="inline-flex items-center gap-1">
          <span>First Connected</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  aria-label="About the First Connected date"
                  className="inline-flex h-4 w-4 items-center justify-center text-gray-400 dark:text-slate-500"
                  onClick={(event) => event.stopPropagation()}
                >
                  <Info className="h-4 w-4" />
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs whitespace-normal font-normal dark:bg-slate-800 dark:text-slate-100 dark:border-slate-700">
                Limited to the date the connection was first discovered by the
                tracking feature, which may differ from when the member actually
                connected.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </span>
      ),
      enableSorting: true,
      cell: ({ row }) => formatDay(row.original.firstConnectedAt),
    },
    {
      accessorKey: "lastDataReceivedAt",
      header: "Last Data Received",
      enableSorting: true,
      cell: ({ row }) =>
        row.original.lastDataReceivedAt ? (
          formatDay(row.original.lastDataReceivedAt)
        ) : (
          <span className="text-gray-500 dark:text-gray-400">Never</span>
        ),
    },
    {
      id: "attribution",
      header: "Attribution",
      cell: ({ row }) =>
        row.original.attributionSource ? (
          attributionLabel(row.original)
        ) : (
          <span className="text-gray-500 dark:text-gray-400">Unset</span>
        ),
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }) => (
        <Badge variant="grey">{connectionStatus(row.original)}</Badge>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <button
          className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          onClick={(event) => {
            event.stopPropagation();
            onEditAttribution(row.original);
          }}
          type="button"
        >
          Edit Attribution
        </button>
      ),
    },
  ];
}

const ConnectionsTable: React.FC<ConnectionsTableProps> = ({
  staleOnly = false,
  noDataMessage = "No connections found",
}) => {
  const {
    connections,
    totalCount,
    loading,
    pagination,
    sorting,
    searchTerm,
    handlePaginationChange,
    handleSortingChange,
    handleSearchChange,
    editingConnection,
    savingAttribution,
    openAttributionDialog,
    closeAttributionDialog,
    saveAttribution,
  } = useAdoptionConnections(staleOnly);

  const columns = useMemo(
    () => createConnectionColumns(openAttributionDialog),
    [openAttributionDialog],
  );

  return (
    <div className="space-y-4">
      <SearchInput
        className="max-w-sm"
        onChange={handleSearchChange}
        placeholder="Search by company name..."
        value={searchTerm}
      />

      <DataTable
        columns={columns}
        data={connections}
        loading={loading}
        noDataMessage={noDataMessage}
        onPageChange={(newPageIndex, newPageSize) =>
          handlePaginationChange({
            pageIndex: newPageIndex,
            pageSize: newPageSize,
          })
        }
        onSortingChange={handleSortingChange}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        rowKeyExtractor={(item) => item.uuid}
        sorting={sorting}
        totalCount={totalCount}
      />

      <AttributionDialog
        connection={editingConnection}
        onClose={closeAttributionDialog}
        onSave={saveAttribution}
        saving={savingAttribution}
      />
    </div>
  );
};

export default ConnectionsTable;
